import {
  CascadiaColor,
  CascadiaMiniIcon,
  CascadiaTypographyStyle,
  decorations,
} from "@certn/cascadia-tokens";
import { css, cx } from "@emotion/css";
import { useMemo } from "react";
import { mergeProps } from "react-aria";
import { Button, ButtonProps } from "react-aria-components";

import { Icon } from "../../foundations/icons/Icon/Icon";
import { getTextStyle } from "../../foundations/typography/styles";
import { useColors } from "../../hooks/useColors";
import { Loader } from "../loaders/loading/Loader";

interface Props extends ButtonProps {
  /**
   * The link style. Passing in `monochrome` will inherit the text colour from the parent.
   * @default default
   * */
  linkStyle?: "monochrome" | "default";
  /**
   * Overrides the text style using a Cascadia typography token.
   * @default form/body-primary-link
   * */
  textStyle?: CascadiaTypographyStyle;
  /**
   * Override the link text color using a Cascadia colour token.
   * @default text-info
   * */
  textColor?: CascadiaColor;
  /** A Cascadia mini icon token that will be displayed to the left of the text. */
  leftIcon?: CascadiaMiniIcon;
  /** A Cascadia mini icon token that will be displayed to the right of the text. */
  rightIcon?: CascadiaMiniIcon;
  /**
   * Adds an underline to the text if set to true.
   * @default true
   * */
  hasUnderline?: boolean;
  isLoading?: boolean;
  testID: string;
}

export function LinkButton(props: Props) {
  const colors = useColors();

  const {
    linkStyle = "default",
    textStyle = "form/body-primary-link",
    textColor = linkStyle === "default" ? "text-info" : undefined,
    leftIcon,
    rightIcon,
    hasUnderline = true,
    isLoading,
    testID,
    ...linkProps
  } = props;

  const className = useMemo(() => {
    return css(textStyle && getTextStyle(textStyle), {
      color: textColor ? colors[textColor] : "inherit",
    });
  }, [colors, textColor, textStyle]);

  return (
    <Button
      {...mergeProps(linkProps, {
        className: cx(
          className,
          hasUnderline && styles.underline,
          (leftIcon || rightIcon) && styles.linkWithIcon,
          styles.link
        ),
      })}
      data-testid={testID}
    >
      {(renderProps) => {
        const children =
          typeof props.children === "function"
            ? props.children(renderProps)
            : props.children;

        if (!leftIcon && !rightIcon && !isLoading) {
          return children;
        }

        const iconColor = renderProps.isDisabled ? "icon-disabled" : textColor;

        return (
          <>
            {leftIcon && <Icon name={leftIcon} type="mini" color={iconColor} />}
            <span className={cx(hasUnderline && styles.underline)}>
              {children}
            </span>
            {rightIcon && (
              <Icon name={rightIcon} type="mini" color={iconColor} />
            )}
            {isLoading && (
              <Loader size="small" overlay background="surface-base" />
            )}
          </>
        );
      }}
    </Button>
  );
}

const styles = {
  link: css({
    display: "inline-flex",
    background: "none",
    border: "none",
    outline: "none",
    borderRadius: "var(--border-radius-2)",
    padding: 0,
    cursor: "pointer",
    position: "relative",
    "&[data-hovered]": {
      backgroundColor: "var(--background)",
    },
    "&[data-disabled]": {
      cursor: "not-allowed",
      color: "var(--text-disabled)",
    },
    "&[data-focus-visible]": {
      boxShadow: "var(--focus-action)",
    },
  }),
  linkWithIcon: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-1)",
    textDecoration: "none",
    width: "fit-content",
  }),
  underline: css({
    textDecoration: decorations["font-deco-underline"],
  }),
};
